"use client"

import {
  PARTNER_LS_CAPTURED_AT_KEY,
  PARTNER_LS_KEY,
  PARTNER_SLUG_PARAM,
  getPartnerAttributionWindowDays,
} from "@/lib/partners/constants"
import {
  buildPartnerSlugSetCookieValue,
  normalizePartnerSlug,
  readPartnerSlugFromDocumentCookie,
} from "@/lib/partners/partner-slug-cookie"
import { getPartnerSlugFromPathname } from "@/lib/partners/partner-path"
import {
  resolvePartnerCapturedAtAtSignup,
  resolvePartnerSlugAtSignup,
} from "@/lib/partners/resolve-signup-partner"

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined"
}

function safeGetLocalStorage(): Storage | null {
  if (!isBrowser()) return null
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function cookieMaxAgeSeconds(): number {
  return getPartnerAttributionWindowDays() * 24 * 60 * 60
}

function persistPartnerSlugCookie(slug: string): void {
  if (!isBrowser()) return
  const secure = window.location.protocol === "https:"
  document.cookie = buildPartnerSlugSetCookieValue(slug, secure, cookieMaxAgeSeconds())
}

function readCapturedAtFromLocalStorage(): string | null {
  const storage = safeGetLocalStorage()
  if (!storage) return null
  const raw = storage.getItem(PARTNER_LS_CAPTURED_AT_KEY)?.trim()
  return raw || null
}

function writeCapturedAtToLocalStorage(iso: string): void {
  const storage = safeGetLocalStorage()
  if (!storage) return
  storage.setItem(PARTNER_LS_CAPTURED_AT_KEY, iso)
}

function readFromLocalStorage(): string | null {
  const storage = safeGetLocalStorage()
  if (!storage) return null
  return normalizePartnerSlug(storage.getItem(PARTNER_LS_KEY))
}

function writeToLocalStorage(slug: string): void {
  const storage = safeGetLocalStorage()
  if (!storage) return
  storage.setItem(PARTNER_LS_KEY, slug)
}

function readFromUrl(): string | null {
  if (!isBrowser()) return null
  try {
    const url = new URL(window.location.href)
    return normalizePartnerSlug(url.searchParams.get(PARTNER_SLUG_PARAM))
  } catch {
    return null
  }
}

function readFromPartnerLandingPath(): string | null {
  if (!isBrowser()) return null
  return getPartnerSlugFromPathname(window.location.pathname)
}

export function capturePartnerFromPage(): void {
  if (!isBrowser()) return

  const fromUrl = readFromUrl()
  const fromPath = readFromPartnerLandingPath()
  const slug = fromUrl ?? fromPath
  if (slug) {
    writeToLocalStorage(slug)
    writeCapturedAtToLocalStorage(new Date().toISOString())
    persistPartnerSlugCookie(slug)
    return
  }

  const fromLs = readFromLocalStorage()
  const fromCookie = readPartnerSlugFromDocumentCookie(document.cookie)
  const existing = fromLs ?? fromCookie
  if (existing) {
    writeToLocalStorage(existing)
    persistPartnerSlugCookie(existing)
  }
}

export function getPartnerSlug(): string | null {
  if (!isBrowser()) return null
  return resolvePartnerSlugAtSignup({
    urlSlug: readFromUrl(),
    localStorageSlug: readFromLocalStorage(),
    cookieSlug: readPartnerSlugFromDocumentCookie(document.cookie),
  }).slug
}

export function getPartnerCapturedAt(): string | null {
  if (!isBrowser()) return null
  const urlSlug = readFromUrl()
  return resolvePartnerCapturedAtAtSignup({
    hasUrlSlug: Boolean(urlSlug),
    localStorageCapturedAt: readCapturedAtFromLocalStorage(),
  })
}

export function initPartnerCapture(): void {
  capturePartnerFromPage()
}

export function getPartnerSignupPayload(): {
  slug: string
  capturedAt: string | null
  source: import("@/lib/partners/resolve-signup-partner").PartnerSignupSource
} | null {
  if (!isBrowser()) return null
  const resolved = resolvePartnerSlugAtSignup({
    urlSlug: readFromUrl(),
    localStorageSlug: readFromLocalStorage(),
    cookieSlug: readPartnerSlugFromDocumentCookie(document.cookie),
  })
  if (!resolved.slug || !resolved.source) return null
  return {
    slug: resolved.slug,
    capturedAt: getPartnerCapturedAt(),
    source: resolved.source,
  }
}
